import React, { useRef, useEffect, useState, useMemo, memo, useCallback, forwardRef, useImperativeHandle } from "react";
import * as d3 from "d3";
import { getMemberWorktimeDetailAPI } from '@/services'
import { useRequest } from 'ahooks'
import { useParams } from "react-router-dom";
import { useWorktime } from './index'
import type { DataItemPos, YDataItem, XDate } from './index'
import clsx from "clsx";
import { Popover, Spin } from 'antd';
import { toHsl } from '@/utils'
import * as lodash from 'lodash'

const config = {
    height: 500,
    marginTop: 20,
    marginRight: 20,
    marginBottom: 50,
    marginLeft: 40
}
interface IProps{

}
const length = (path?: string | null) => {
    if(!path) return 0
    const node = d3.create("svg:path").attr("d", path).node() as SVGPathElement
    if (!node) return 0
    return node.getTotalLength()
}
interface HoverParams{
    params: Req.DashboardWorktimeDetail,
    pos: { x: number, y: number },
    label: string
}
interface ISvgChart{
    xData: XDate[]
    data: YDataItem[]
    min: number
    max: number
    today?: XDate,
    onItemHover: (v: HoverParams) => void,
    onItemHoverEnd: ()=>void
}
let timer: NodeJS.Timer | null = null
const SvgChart: React.FC<ISvgChart> = memo((props) => {
    const {
        min,
        max,
        xData,
        data,
        today
    } = props
    
    const svgRef = useRef<SVGSVGElement>(null);
    const gx = useRef<SVGGElement>(null);
    const gy = useRef<SVGGElement>(null);
    const [svgWidth, setSvgWidth] = useState(500)

    useEffect(() => {
        if (!gx.current || !gy.current) return
        const {
            height,
            marginBottom,
            marginLeft
        } = config

        const xScale = d3.scaleBand() // 使用band比例尺来处理离散的字符串数据
            .domain((xData || []).map(v => v.label)) // 定义坐标轴的字符串值
            .range([marginLeft, svgWidth]) // SVG中的像素范围
        

        const y = d3.scaleLinear()
            .domain([min, max]) // 数据的最小值和最大值
            .range([height - marginBottom, marginBottom]); // SVG中的像素范围;

        const yAxisTicks = y.ticks()
            .filter(Number.isInteger);
        const yAxis = d3.axisLeft(y)
            .tickValues(yAxisTicks)
            .tickFormat(d3.format('d'));

        d3.select(gx.current).call(d3.axisBottom(xScale))
        d3.select(gy.current).selectAll(".tick-line-dashed").remove()
        d3.select(gy.current).call(yAxis).call(g => g.select(".domain").remove())
            .call(g => g.selectAll(".tick line").clone()
                .attr("class", "tick-line-dashed")
                .attr("stroke-opacity", 0.2)
                .attr("stroke-dasharray", "3, 3")
                .attr("x2", svgWidth - marginLeft)
            )
        const svg = d3.select(svgRef.current)
        const getXPosByLabel = (label: string) => {
            return (xScale(label) || 0) + xScale.bandwidth() / 2
        }
        const getXPos = (d: DataItemPos) => {
            return (xScale(d.xLabel) || 0) + xScale.bandwidth() / 2
        }
        const getYPos = (d: DataItemPos) => y(d.yValue)
        // 创建折线路径生成器
        const line = d3.line<DataItemPos>()
            .x(getXPos)
            .y(getYPos);
        svg.selectAll("g.member-item").remove()
        svg.selectAll("text.hover-member-name").remove()
        const text = svg.append("text").attr("class", "hover-member-name")

        // 绘制折线
        data.filter(v => !v.hide).forEach(v => {
            const solidData = v.data.filter(item => !item.isAfter || item.isToday)
            const dashedData = v.data.filter(item => item.isAfter || item.isToday)
            const g = svg.append("g").attr("class", "member-item")

            g.on("mouseover", function (event: MouseEvent) {
                g.raise()
                const [xm, ym] = d3.pointer(event);
                text.text(v.user.lastname)
                    .attr("x", xm - 16)
                    .attr("y", ym - 10)
                    .attr("display", null);
            }, { capture: true })
            .on("mouseout", function () {
                text.attr("display", "none")
            }, { capture: true });

            const solidLen = length(line(solidData));
            const dashedLen = length(line(dashedData));

            g.append("path").lower()
                .datum(solidData)
                .attr("class", "data-line")
                .attr("fill", "none")
                .attr("stroke", v.color)
                .attr("stroke-width", 2)
                .attr("stroke-dasharray", `0,${solidLen}`)
                .attr("d", line(solidData)) // 使用路径生成器绘制路径
                .transition()
                .duration(600)
                .ease(d3.easeLinear)
                .attr("stroke-dasharray", `${solidLen},${solidLen}`)

            // 虚线动画 - 画完整虚线搭配一条实线
            const dashing = "6, 3"
            const dashCount = Math.ceil(dashedLen / (6 + 3));
            const newDashes = new Array(dashCount).join(dashing + " ");
            const dashArray = newDashes + " 0, " + dashedLen;
            g.append("path").lower()
                .datum(dashedData)
                .attr("class", "data-line")
                .attr("fill", "none")
                .attr("stroke", v.color)
                .attr("stroke-width", 2)
                .attr("d", line(dashedData)) // 使用路径生成器绘制路径
                .attr("stroke-dashoffset", dashedLen)
                .attr("stroke-dasharray", dashArray)
                .transition()
                .duration(600)
                .delay(600)
                .ease(d3.easeLinear)
                .attr("stroke-dashoffset", 0);


            g.selectAll("circle.data-circle")
                .data(v.data)
                .join("circle")
                .attr("class", "data-circle")
                .attr("cx", getXPos) // 将数据点的x坐标映射到SVG的x坐标
                .attr("cy", getYPos) // 将数据点的y坐标映射到SVG的y坐标
                .attr("r", 3) // 圆的半径
                .attr("stroke-width", 2)
                .attr("fill", "#ffffff")
                .attr("stroke", v.color) // 圆的填充颜色
                .on("mouseover", function (event: MouseEvent, d) {
                    if (timer) {
                        window.clearTimeout(timer)
                        timer = null
                    }
                    props.onItemHover({
                        params: {
                            startDate: d.date?.startDate || "",
                            endDate: d.date?.endDate || "",
                            userID: v.user.id
                        },
                        pos: {
                            x: event.clientX,
                            y: event.clientY
                        },
                        label: d.date?.dateLabel || ""
                    })
                    // // 悬停时的动画效果
                    d3.select(this)
                        .transition()
                        .duration(200)
                        .attr("r", 6)
                })
                .on("mouseout", function () {
                   
                    // 鼠标移出时的动画效果
                    d3.select(this)
                        .transition()
                        .duration(200)
                        .attr("r", 3)
                        .on("end", () => {
                            timer = setTimeout(() => {
                                props.onItemHoverEnd()
                            }, 300)
                        })
                });
            
            
        })
        svg.selectAll("line.today").remove()
        if (today) {
            const x = getXPosByLabel(today.label)
            svg.append("line")
                .lower()
                .attr("class", "today")
                .attr("x1", x) // x1 为0，表示从最左边开始
                .attr("x2", x) // x2 为0，表示到最左边结束
                .attr("y1", 0) // y1 为顶部边距
                .attr("y2", height - marginBottom) // y2 为底部边距
                .attr("stroke", "#dbeafe") // 线条颜色
                .attr("stroke-width", 12)
                .attr("stroke-dasharray", `${config.height}`)
                .attr("stroke-dashoffset", config.height)
                .transition()
                .duration(600)
                .ease(d3.easeLinear)
                .attr("stroke-dashoffset", 0);
        }

    }, [gx, gy, svgWidth, data, xData]);


    useEffect(() => {
        const resize = () => {
            if (!svgRef.current) return
            setSvgWidth(svgRef.current.parentElement?.clientWidth || 500)
        }
        resize()
        window.addEventListener("resize", resize)
        return () => {
            window.removeEventListener("resize", resize)
        }
    }, [setSvgWidth, svgRef])
    
    return (
        <svg ref={svgRef} width={svgWidth} height={config.height}>
            <g ref={gx} transform={`translate(0,${config.height - config.marginBottom + 20})`} />
            <g ref={gy} transform={`translate(${config.marginLeft}, 0)`} />
            {/* <path fill="none" stroke="currentColor" strokeWidth="1.5" d={line(data)} /> */}
            {/* <g fill="white" stroke="currentColor" strokeWidth="1.5">
                {data.map((d, i) => (<circle key={i} cx={x(i)} cy={y(d)} r="2.5" />))}
            </g> */}
        </svg>
    )
})
export const Worktime: React.FC<IProps> = () => {
    const { projectID } = useParams();

    const [yData, setYData] = useState<YDataItem[]>([])
    const [yAxis, setYAxis] = useState({ min: 0, max: 1 })
    const { data: worktime } = useWorktime(projectID || 0, {
        onSuccess: (d) => {
            if (!d.yData) return
            setYAxis({
                min: d.min,
                max: d.max
            })
            setYData(d.yData)
        }
    })
    
    const onUserVisibleChange = (item: YDataItem) => {
        const hasHidden = yData.find(v => v.hide)
        let newYData: YDataItem[]
        if (!hasHidden) {
            // 所有都选中时，点击只选中该用户
            newYData = yData.map(d => {
                if (item.user.id === d.user.id) {
                    return {
                        ...d,
                        hide: false
                    }
                }
                return {
                    ...d,
                    hide: true
                }
            })
        } else {
            newYData = yData.map(d => {
                if (item.user.id === d.user.id) {
                    return {
                        ...d,
                        hide: !d.hide
                    }
                }
                return {
                    ...d
                }
            })
        }
        
        let min: number, max: number
        newYData.forEach(v => {
            if (v.hide) return
            v.data.forEach(pos => {
                if (min == null) {
                    min = pos.yValue
                } else {
                    min = Math.min(min, pos.yValue)
                }
                if (max == null) {
                    max = pos.yValue
                } else {
                    max = Math.max(max, pos.yValue)
                }
            })
        })
        setYAxis({min: min!, max: max!})
        setYData(newYData)
    }
    const { data: memberTask, loading: memberTaskLoading, run: fetchMemberTask } = useRequest(getMemberWorktimeDetailAPI, {
        manual: true,
    })
    const hoverItem = useRef<HoverParams>()
    const popRef = useRef<IPopHandler>(null)
    const onItemHover = useCallback((data: HoverParams) => {
        if (memberTaskLoading) return
        hoverItem.current = { ...data }
        fetchMemberTask(projectID || 0, data.params)
        popRef.current?.onShow()
    }, [])
    const onItemHoverEnd = useCallback(() => {
        popRef.current?.onHide()
    }, [])
    const onCheckAll = () => {
        const hasHide = yData.find(v => v.hide)
        if (hasHide) {
            // 全选显示
            if(!worktime) return
            setYAxis({
                min: worktime.min,
                max: worktime.max
            })
        }
        setYData(v => v.map(d => {
            return {
                ...d,
                hide: hasHide ? false : true
            }
        }))
    }
    return (
        <div className="w-full mt-4 relative">
            {/* <div className="py-2 border-0 border-b border-solid border-gray-200 font-bold text-sm">成员负荷 <span className=" font-light text-xs"> （任务数）</span></div> */}
            <div className="flex justify-center flex-wrap gap-2 mt-4">
                <div className="cursor-pointer px-1 select-none hover:font-bold" onClick={onCheckAll}>全部</div>
                {yData.map(v => (
                    <div key={v.user.id} className={clsx("flex items-center cursor-pointer select-none", v.hide && "grayscale opacity-20")} onClick={()=>onUserVisibleChange(v)}>
                        <div className="relative w-6 h-3 flex items-center justify-center">
                            <div className="h-[2px] absolute left-0 w-full top-1/2 -translate-y-1/2 z-0" style={{backgroundColor: v.color}}></div>
                            <div className="h-3 w-3 rounded-full border-2 border-solid bg-white z-10" style={{borderColor: v.color}}></div>
                        </div>
                        <div className={clsx("ml-1", v.hide && "text-gray-600")}>{v.user.lastname}</div>
                    </div>
                ))}
            </div>
            <div className="mt-4 relative">
                {worktime?.today && (
                    <div className="relative flex h-[40px]">
                        <Popover content={
                            <div className="max-h-80 overflow-y-auto">
                                <div className="font-bold">{worktime?.today?.dateLabel}</div>
                                <div className="flex gap-3 mt-2">
                                    <div className="w-16"></div>
                                    <div className="w-12 font-bold">任务数</div>
                                    <div className="w-12 font-bold">项目数</div>
                                    <div className="w-12 font-bold">进度</div>
                                </div>

                                {
                                    worktime.todayData.filter(v => yData.filter(y => !y.hide).map(y => y.user.id).includes(v.user.id)).map((v, i) => (
                                        <div key={i} className="flex gap-3 mt-2">
                                            <div className="w-16">{v.user.lastname}</div>
                                            <div className="w-12">{v.taskCount}</div>
                                            <div className="w-12">{v.projectCount}</div>
                                            <div className="w-12">{getFixedProgress(v.progress)}%</div>
                                        </div>
                                    ))
                                }
                            </div>
                        } placement="bottom">
                            <div className="w-[200px] h-[40px] bg-blue-100 text-sky-900 rounded-md ml-[20px] flex items-center justify-center absolute left-1/2 -translate-x-1/2">本周（{worktime?.today?.label}）</div>
                        </Popover>

                    </div>
                )}
                <div className="absolute left-0 font-bold text-sm">成员负荷 <span className=" font-light text-xs"> （任务数）</span></div>
            </div>
            
            {worktime && (
                <SvgChart
                    onItemHover={onItemHover}
                    onItemHoverEnd={onItemHoverEnd}
                    data={yData}
                    xData={worktime.xDates}
                    min={yAxis.min}
                    max={yAxis.max}
                    today={worktime.today}
                />
            )}
            <MemberTaskPop ref={popRef} hoverItem={hoverItem.current} loading={memberTaskLoading} data={memberTask} pos={hoverItem.current?.pos} />
        </div>
    );
}
interface IMemberTaskPop{
    data?: API.Project.DashboardWorktimeDetail,
    hoverItem?: HoverParams
    pos?: {
        x: number,
        y: number
    },
    loading: boolean
}
export interface IPopHandler {
    onShow: () => void
    onHide: () => void
}
const getFixedProgress = (rate: number) => {
    if (!rate) return 0
    return Number(rate).toFixed(1)
}
const MemberTaskPop = forwardRef<IPopHandler, IMemberTaskPop>((props, ref) => {
    const [visible, setVisible] = useState(false)
    const popRef = useRef<HTMLDivElement>(null)
    const focusPopRef = useRef(false)
    const isTriggerOut = useRef(false)
    const popH = 200
    const popW = 200
    const position = useMemo(() => {
        if (!props.pos) return {
            x: 0,
            y: 0
        }
        const pos = props.pos
        const boxW = window.document.documentElement.clientWidth
        const boxH = window.document.documentElement.clientHeight
        let y = pos.y - popH - 12
        if (y + popH > boxH) {
            y = boxH - popH
        }
        let x = pos.x - (popW / 2)
        if (x + popW > boxW) {
            x = boxW - popW
        }

        return {
            x, y
        }
    }, [props.pos])

    useEffect(() => {
        
        const mouseEvt = lodash.debounce((e: MouseEvent) => {
            if (!popRef.current) {
                focusPopRef.current = false
                return
            }
            const rect = popRef.current.getBoundingClientRect()
            if (e.clientX >= rect.left && e.clientX <= rect.left + rect.width
            && e.clientY >= rect.top && e.clientY <= rect.top + rect.height) {
                focusPopRef.current = true
                return
            }
            focusPopRef.current = false
            if (isTriggerOut.current) {
                setVisible(() => false)
            }
        }, 100)
        window.addEventListener("mousemove", mouseEvt)

        return () => {
            window.removeEventListener("mousemove", mouseEvt)
        }
    }, [])

    useImperativeHandle(ref, () => {
        return {
            onShow: () => {
                isTriggerOut.current = false
                focusPopRef.current = false
                setVisible(()=>true)
            },
            onHide: () => {
                if (focusPopRef.current) {
                    isTriggerOut.current = true
                    return
                }
                setVisible(() => false)
            }
        };
    }, []);

    return (
        <div ref={popRef} className={clsx("fixed rounded-lg bg-white box-shadow z-200 text-[12px]", !visible && "hidden")} style={{ left: position.x, top: position.y, width: popW, height: popH}}>
            <div className="h-full p-4 overflow-y-auto flex flex-col justify-between">
                    <div className="relative">
                        <div className="font-bold">{props.hoverItem?.label}</div>
                        <div className="font-bold mt-2">
                            本项目任务({props.data?.packages.length})
                        </div>
                        <div>
                            {props.data?.packages.map(v => (
                                <div key={v.id} className="flex justify-between mt-2">
                                    <span>{v.subject}</span>
                                    <span>{getFixedProgress(v.derivedDoneRatio > 0 ? v.derivedDoneRatio : v.doneRatio)}%</span>
                                </div>
                            ))}
                        </div>
                        <div className="absolute right-0 top-0 w-8 h-8 overflow-hidden rounded-full text-white whitespace-nowrap flex items-center justify-center" style={{ backgroundColor: toHsl(props.data?.user.lastname || props.data?.user.firstname || "") }}>
                                {props.data?.user.lastname}
                        </div>
                    </div>
                    <div>
                        <div className="bg-gray-200 h-px mt-4"></div>
                        <div className="font-bold mt-4 flex justify-between">
                            <span>其他项目数</span>
                            <span>{props.data?.otherProjectCount}</span>
                        </div>
                        <div className="font-bold mt-1 flex justify-between">
                            <span>其他任务数</span>
                        <span>{props.data?.otherTaskCount}</span>
                        </div>
                    </div>
            </div>
            {props.loading && (
                <div className="absolute h-full w-full left-0 top-0 flex items-center justify-center bg-gray-200/50">
                    <Spin></Spin>
                </div>
            )}
        </div>
    )
})